/**
 * ml_models_lifecycle ClearML gateway client (superuser-only).
 *
 * BE: services/plugins/ml_models_lifecycle/api/. Mounted at /ml_models_lifecycle/.
 * Lets staff browse the ClearML dataset hierarchy, lock/download a dataset for
 * annotation, and (later) upload a corrected version back.
 */
import { axiosInstance } from './api';

export type DownloadStatus =
  | 'pending' | 'downloading' | 'ready' | 'uploading' | 'done' | 'failed' | 'discarded';

export interface DatasetVersionNode {
  version_id: string;
  version: string | null;
  created: number | null;
  /** Set only when an ACTIVE local copy of THIS version exists (open/delete it). */
  downloaded: { id: number; status: DownloadStatus } | null;
  /** Most recent copy's status (incl. terminal done/discarded/failed), or null. */
  last_status: DownloadStatus | null;
  last_download_id: number | null;
}

export interface DatasetNode {
  name: string;
  error: string;
  versions: DatasetVersionNode[];
}

export interface ZoneNode {
  zone_id: number | null;  // null = the global "Отменённые алерты" (declined) node
  zone_name: string;
  folder_id: string;
  project_name: string;
  datasets: DatasetNode[];
  is_declined?: boolean;
}

export interface HierarchyResponse {
  zones: ZoneNode[];
  cached: boolean;
}

export interface DownloadedDataset {
  id: number;
  clearml_folder_id: string;
  dataset_name: string;
  version_id: string;
  status: DownloadStatus;
  status_detail: string;
  task_id: string;
  new_version_id: string;
  local_path: string;
  downloaded_by: number | null;
  downloaded_by_username: string | null;
  downloaded_at: string;
  updated_at: string;
  is_active: boolean;
}

export const getHierarchy = (refresh = false) =>
  axiosInstance.get<HierarchyResponse>('/ml_models_lifecycle/hierarchy/', {
    params: refresh ? { refresh: 1 } : {},
  });

export const getActiveDownloads = () =>
  axiosInstance.get<DownloadedDataset[]>('/ml_models_lifecycle/datasets/downloads/', {
    params: { active: 1 },
  });

export const startDownload = (
  folder_id: string,
  dataset_name: string,
  version_id: string,
) =>
  axiosInstance.post<DownloadedDataset>('/ml_models_lifecycle/datasets/download/', {
    folder_id, dataset_name, version_id,
  });

export const getDownload = (id: number) =>
  axiosInstance.get<DownloadedDataset>(`/ml_models_lifecycle/datasets/download/${id}/status/`);

/** Push the local annotations.json to ClearML as a new version (keeps local copy).
 *  `description` (optional) is recorded on the new ClearML dataset task. */
export const uploadNewVersion = (downloaded_dataset_id: number, description = '') =>
  axiosInstance.post('/ml_models_lifecycle/datasets/upload-version/', { downloaded_dataset_id, description });

/** Delete the local copy (frees the dataset for training; drops un-uploaded edits). */
export const deleteLocalCopy = (downloaded_dataset_id: number) =>
  axiosInstance.post(`/ml_models_lifecycle/datasets/${downloaded_dataset_id}/discard/`);

// ── annotation (table_control) ──────────────────────────────────────────────
export type AnnotationSubtype = 'presence' | 'state';

export interface CropItem {
  table_id: number;
  table: string;
  frame_id: number;
  camera_id: number | null;
  camera: string | null;
  crop_url: string;
  // null = not yet labelled by a human (only for source='pending-human' crops, so a
  // multi-session labeller can resume partial progress without an unset field reading
  // as a real false). Committed crops always carry booleans.
  judgement: Record<string, boolean | null>;
  presence: Record<string, boolean | null>;
  state: Record<string, boolean | null>;
  derived: { zone_empty: boolean; table_empty: boolean };
  source: string | null;
  annotated_by: string | null;
  annotated_at: string | null;
}

export interface CropsResponse {
  total: number;
  dataset: {
    id: number;
    name: string;
    zone_name: string | null;
    version_id: string;
    version: string | null;
  };
  classes: { judgement: string[]; presence: string[]; state: string[] };
  items: CropItem[];
}

/** Returns ALL crops of the local copy (the grid renders them at once). */
export const getCrops = (ddId: number) =>
  axiosInstance.get<CropsResponse>(`/table_control/crops/${ddId}/`);

export type CropLabelDelta = { table_id: number; frame_id: number } & Record<string, boolean>;

/** Bulk-save changed crop records into the local annotations.json (debounced caller). */
export const annotateBulk = (ddId: number, items: CropLabelDelta[], source = 'user') =>
  axiosInstance.post<{ saved: number; total: number }>(
    `/table_control/crops/${ddId}/annotate/`,
    { items, source },
  );

/** Delete a crop entirely (image + annotation; removed from the next ClearML version). */
export const deleteCrop = (ddId: number, table_id: number, frame_id: number) =>
  axiosInstance.post<{ deleted: string; total: number }>(
    `/table_control/crops/${ddId}/delete/`,
    { table_id, frame_id },
  );

// Cancelled (declined) table alerts are mined as hard negatives via the standard
// crop annotator: collect feeds them into the global `table-declined-alerts`
// dataset, the operator labels it like any dataset, distribute splits the labelled
// crops into the per-zone datasets. No bespoke review endpoint anymore.

// ── shelf-percentage annotation (empty_shelf / выкладка) ────────────────────
// The label is a single `percentage` float in [0, 100] (not the table booleans),
// keyed by (shelf_id, frame_id). Endpoints mirror the table ones under /empty_shelf/.
export interface ShelfCropItem {
  shelf_id: number;
  shelf: string;
  rack_id: number | null;
  frame_id: number;
  camera_id: number | null;
  camera: string | null;
  crop_url: string;
  percentage: number | null;
  source: string | null;
  annotated_by: string | null;
  annotated_at: string | null;
}

export interface ShelfCropsResponse {
  total: number;
  dataset: {
    id: number;
    name: string;
    zone_name: string | null;
    version_id: string;
    version: string | null;
  };
  label: { field: string; min: number; max: number };
  items: ShelfCropItem[];
}

export const getShelfCrops = (ddId: number) =>
  axiosInstance.get<ShelfCropsResponse>(`/empty_shelf/crops/${ddId}/`);

export type ShelfLabelDelta = { shelf_id: number; frame_id: number; percentage?: number };

/** Bulk-save changed shelf-crop %-labels into the local annotations.json (debounced). */
export const annotateShelfBulk = (ddId: number, items: ShelfLabelDelta[], source = 'user') =>
  axiosInstance.post<{ saved: number; total: number }>(
    `/empty_shelf/crops/${ddId}/annotate/`,
    { items, source },
  );

/** Delete a shelf crop entirely (image + annotation; dropped from the next version). */
export const deleteShelfCrop = (ddId: number, shelf_id: number, frame_id: number) =>
  axiosInstance.post<{ deleted: string; total: number }>(
    `/empty_shelf/crops/${ddId}/delete/`,
    { shelf_id, frame_id },
  );

// ── annotation kinds by dataset name ────────────────────────────────────────
// Each point has a small, known set of datasets, and each is annotated a
// specific way. This registry maps the dataset name → annotation kind so the UI
// knows (at download/browse time) how a dataset will be labeled. To add a kind:
// register its name here, add a branch in DataPage's AnnotatorSwitch, and an
// i18n label (data.kind.*). Unknown names fall back to 'table_crops' (the
// original default) so the tool never hard-fails on an unrecognized name.
export const TABLE_CROPS_DATASET_NAME = 'table-occupancy-crops';
export const AUDIO_DATASET_NAME = 'audio-transcripts';
export const SHELF_CROPS_DATASET_NAME = 'shelf-percentage-crops';
export const SHELF_DECLINED_DATASET_NAME = 'shelf-percentage-declined';

export type AnnotationKind = 'table_crops' | 'audio' | 'shelf_percentage';

export const DATASET_KINDS: Record<string, AnnotationKind> = {
  [TABLE_CROPS_DATASET_NAME]: 'table_crops',
  [AUDIO_DATASET_NAME]: 'audio',
  [SHELF_CROPS_DATASET_NAME]: 'shelf_percentage',
  [SHELF_DECLINED_DATASET_NAME]: 'shelf_percentage',
};

/** Annotation kind for a dataset name (defaults to table_crops for unknowns). */
export const datasetKind = (name: string): AnnotationKind => DATASET_KINDS[name] ?? 'table_crops';

// ── audio-transcript annotation (speech_to_text) ────────────────────────────

export interface AudioUtterance { start: number; end: number; text: string; }

export interface AudioClipItem {
  audio_record_id: number;
  audio_url: string;
  recorded_at: string | null;
  /** Large-RU-model draft to fix (pre-fill). */
  model_text: string;
  /** Prod small-model output, for reference. */
  small_model_text: string;
  model_segments: AudioUtterance[];
  /** Human gold: null = unlabeled, "" = intentional noise/inaudible, text = corrected. */
  corrected_text: string | null;
  labeled: boolean;
  annotated_by: string | null;
  annotated_at: string | null;
}

export interface AudioClipsResponse {
  total: number;
  labeled: number;
  dataset: { id: number; name: string; version_id: string };
  items: AudioClipItem[];
}

/** All clips of the downloaded audio-transcripts copy. */
export const getAudioClips = (ddId: number) =>
  axiosInstance.get<AudioClipsResponse>(`/speech_to_text/datasets/${ddId}/clips/`);

/** Save corrected transcripts into the local transcripts.json (corrected_text:
 *  "" = intentional noise target; the verbatim gold otherwise). */
export const annotateAudio = (
  ddId: number,
  items: { audio_record_id: number; corrected_text: string | null }[],
) =>
  axiosInstance.post<{ saved: number; total: number; labeled: number }>(
    `/speech_to_text/datasets/${ddId}/annotate/`,
    { items },
  );

// ── bootstrap (Claude-Vision cold-start labelling of a new point) ────────────

export interface BootstrapTable {
  table_id: number;
  name: string;
  available_crops: number;
  enough: boolean;
}

export interface BootstrapPrecheck {
  zone_id: number;
  onboarded: boolean;
  ready: boolean;
  since_days: number;
  target_per_table: number;
  tables: BootstrapTable[];
  warning: string | null;
}

export interface BootstrapRun {
  id: number;
  zone_id: number;
  status: 'running' | 'done' | 'failed';
  crops: number;
  labelled: number;
  per_table: Record<string, number>;
  dataset_version_id: string;
  since_days: number;
  max_per_table: number;
  vision_model: string;
  error: string;
  dagster_run_url: string;
  created_at: string;
  finished_at: string | null;
}

/** Pre-flight: how many crops each table would get right now. DB-only, costs nothing. */
export const getBootstrapPrecheck = (zone_id: number) =>
  axiosInstance.get<BootstrapPrecheck>('/ml_models_lifecycle/bootstrap/precheck/', {
    params: { zone_id },
  });

/** Launch the Claude-Vision bootstrap for a zone. Spends real money per crop. */
export const launchBootstrap = (zone_id: number, max_per_table?: number) =>
  axiosInstance.post<{ run_id: string; status: string }>('/ml_models_lifecycle/bootstrap/', {
    zone_id,
    ...(max_per_table ? { max_per_table } : {}),
  });

/** Past runs for the zone — shown next to the button so a re-run is an informed choice. */
export const getBootstrapRuns = (zone_id: number) =>
  axiosInstance.get<BootstrapRun[]>('/ml_models_lifecycle/bootstrap/runs/', {
    params: { zone_id },
  });
